import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  tableCellClasses,
} from '@mui/material';
import { styled as muiStyled, useTheme } from '@mui/material/styles';
import React from 'react';

import { ActivityChip, AttestationChip } from './monitoring-chip';
import {
  computeAttestationGap,
  getMonitoringStatus,
  validateMonitoringJoin,
} from './monitoring-status-provider';
import ModalComponent from '../../../project-table/archive-project-modal';

import Button from '~/client/components/base/button';
import { getDesignTokens } from '~/client/components/base/core';
import { ArrowRight } from '~/client/components/base/icon';
import SearchAndFiltersBar from '~/client/components/base/search-and-filters-bar';
import { TableAction } from '~/client/components/base/table';
import Text from '~/client/components/base/text';
import PolicyItem from '~/client/components/policy/policy-item';
import * as GQL from '~/graphql/generated/client';

const StyledTableCell = muiStyled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    fontSize: '14px',
    fontWeight: 600,
    padding: '16px 12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: '16px 12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    fontStyle: 'normal',
    fontWeight: 'normal',
  },
}));

const StyledTableRow = muiStyled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  cursor: 'pointer',
}));

type ActivityFilter = '' | 'active' | 'idle';
type AttestationFilter = '' | 'attested' | 'unverified';

interface Props {
  assessments: GQL.AssessmentFragment[];
  canManageAssessments: boolean;
  removeAssessmentHandler: (id: string) => void;
  removeAssessmentLoading: boolean;
  onRowClick: (assessmentId: string) => void;
  className?: string;
}

interface PolicyEntry {
  assessment: GQL.AssessmentFragment;
  policyId: string;
  policyName: string;
  active: number;
  total: number;
  totalConsults: number;
  attestationGap: number;
  everExercised: boolean;
  hasSummary: boolean;
}

function buildPolicyEntry(
  assessment: GQL.AssessmentFragment
): PolicyEntry | null {
  const policy = assessment.policy;
  if (!policy) {
    return null;
  }

  const policyId = policy.identifier;
  const policyName = policy.name;
  const result = getMonitoringStatus(policyId);

  if (result.error || !result.data) {
    return {
      assessment,
      policyId,
      policyName,
      active: 0,
      total: 0,
      totalConsults: 0,
      attestationGap: 0,
      everExercised: false,
      hasSummary: false,
    };
  }

  const snapshot = result.data;
  const controlMap = new Map<string, unknown>();
  for (const bundle of policy.controlBundleList ?? []) {
    for (const ctrl of bundle.controls ?? []) {
      controlMap.set(ctrl.identifier, ctrl);
    }
  }
  validateMonitoringJoin(snapshot, Array.from(controlMap.keys()));

  const activeCount = snapshot.controls.filter(
    (c) => c.activity === 'active'
  ).length;
  const attestationGap = computeAttestationGap(snapshot.controls);
  const everExercised = activeCount > 0 || snapshot.summary.totalConsults > 0;

  return {
    assessment,
    policyId,
    policyName,
    active: activeCount,
    total: snapshot.summary.totalControls,
    totalConsults: snapshot.summary.totalConsults,
    attestationGap,
    everExercised,
    hasSummary: true,
  };
}

const MonitoringTab: React.FC<Props> = ({
  assessments,
  canManageAssessments,
  removeAssessmentHandler,
  removeAssessmentLoading,
  onRowClick,
  className,
}) => {
  const theme = useTheme();
  const t = React.useMemo(
    () => getDesignTokens(theme.palette.mode, theme.accentPalette),
    [theme.palette.mode, theme.accentPalette]
  );

  const [searchInput, setSearchInput] = React.useState('');
  const [activityFilter, setActivityFilter] =
    React.useState<ActivityFilter>('');
  const [attestationFilter, setAttestationFilter] =
    React.useState<AttestationFilter>('');

  const entries = React.useMemo(
    () =>
      assessments
        .map(buildPolicyEntry)
        .filter((e): e is PolicyEntry => e !== null),
    [assessments]
  );

  const filteredEntries = React.useMemo(() => {
    let result = entries;
    if (searchInput) {
      const lower = searchInput.toLowerCase();
      result = result.filter((e) => e.policyName.toLowerCase().includes(lower));
    }
    if (activityFilter === 'active') {
      result = result.filter((e) => e.active > 0);
    } else if (activityFilter === 'idle') {
      result = result.filter((e) => e.hasSummary && e.active === 0);
    }
    if (attestationFilter === 'attested') {
      result = result.filter(
        (e) => e.hasSummary && e.attestationGap === 0 && e.everExercised
      );
    } else if (attestationFilter === 'unverified') {
      result = result.filter((e) => e.attestationGap > 0);
    }
    return result;
  }, [entries, searchInput, activityFilter, attestationFilter]);

  if (entries.length === 0) {
    return (
      <Box sx={{ pt: 2 }}>
        <Text sz="body14" color="secondary">
          No monitoring controls attached to this project yet.
        </Text>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 2 }}>
      <SearchAndFiltersBar
        placeholder="Search monitoring policies..."
        value={searchInput}
        onChange={setSearchInput}
      >
        <FormControl size="small" className="filter-field">
          <InputLabel>Activity</InputLabel>
          <Select
            value={activityFilter}
            label="Activity"
            onChange={(e) =>
              setActivityFilter(e.target.value as ActivityFilter)
            }
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="idle">Idle</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" className="filter-field">
          <InputLabel>Attestation</InputLabel>
          <Select
            value={attestationFilter}
            label="Attestation"
            onChange={(e) =>
              setAttestationFilter(e.target.value as AttestationFilter)
            }
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="attested">Attested</MenuItem>
            <MenuItem value="unverified">Attestation gap</MenuItem>
          </Select>
        </FormControl>
      </SearchAndFiltersBar>

      <Box sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell align="left">NAME</StyledTableCell>
              <StyledTableCell align="left">ACTIVITY</StyledTableCell>
              <StyledTableCell align="left">ATTESTATION</StyledTableCell>
              <StyledTableCell align="left">CONSULTS</StyledTableCell>
              {canManageAssessments && (
                <StyledTableCell align="center">ACTIONS</StyledTableCell>
              )}
              <StyledTableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEntries.map((entry) => {
              const activeAttested = entry.active - entry.attestationGap;
              const activeGap = entry.attestationGap;
              const idleCount = entry.total - entry.active;
              const hasSegments = entry.total > 0;

              const rollupAttestation:
                | 'attested'
                | 'unverified'
                | 'not_applicable' =
                !entry.hasSummary || !entry.everExercised
                  ? 'not_applicable'
                  : entry.attestationGap > 0
                    ? 'unverified'
                    : 'attested';

              return (
                <StyledTableRow
                  key={entry.assessment._id}
                  onClick={() => onRowClick(entry.assessment._id)}
                >
                  <StyledTableCell>
                    {entry.assessment.policy ? (
                      <PolicyItem
                        policy={entry.assessment.policy}
                        governanceTab="monitoring"
                      />
                    ) : (
                      <Text sz="body14">{entry.policyName}</Text>
                    )}
                  </StyledTableCell>

                  <StyledTableCell>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        minWidth: 100,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          height: 5,
                          borderRadius: '3px',
                          overflow: 'hidden',
                        }}
                      >
                        {hasSegments ? (
                          <>
                            {activeAttested > 0 && (
                              <Box
                                sx={{
                                  flex: activeAttested,
                                  backgroundColor: t.status.success,
                                }}
                              />
                            )}
                            {activeGap > 0 && (
                              <Box
                                sx={{
                                  flex: activeGap,
                                  backgroundColor: t.status.warning,
                                }}
                              />
                            )}
                            {idleCount > 0 && (
                              <Box
                                sx={{
                                  flex: idleCount,
                                  backgroundColor: t.status.neutral,
                                }}
                              />
                            )}
                          </>
                        ) : (
                          <Box
                            sx={{
                              flex: 1,
                              backgroundColor: t.status.neutral,
                              opacity: 0.3,
                            }}
                          />
                        )}
                      </Box>
                      <Text sz="body12" sx={{ color: t.textMuted }}>
                        {entry.hasSummary
                          ? `${entry.active}/${entry.total} active`
                          : 'Signal unavailable'}
                      </Text>
                    </Box>
                  </StyledTableCell>

                  <StyledTableCell>
                    {rollupAttestation === 'not_applicable' ? (
                      <Text sz="body12" sx={{ color: t.textMuted }}>
                        —
                      </Text>
                    ) : (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <AttestationChip value={rollupAttestation} />
                        {entry.attestationGap > 0 && (
                          <Text sz="body12" sx={{ color: t.status.warning }}>
                            ({entry.attestationGap})
                          </Text>
                        )}
                      </Box>
                    )}
                  </StyledTableCell>

                  <StyledTableCell>
                    <Text sz="body14" sx={{ color: t.textPrimary }}>
                      {entry.hasSummary ? entry.totalConsults : '—'}
                    </Text>
                  </StyledTableCell>

                  {canManageAssessments && (
                    <StyledTableCell align="center">
                      <ModalComponent
                        {...{
                          className,
                          archiveProjectHandler: removeAssessmentHandler,
                          entry: {
                            id: entry.assessment._id,
                            projectName: entry.policyName,
                          },
                          archiveProjectLoading: removeAssessmentLoading,
                          confirmText: `Are you sure you want to remove monitoring policy '${entry.policyName}'?`,
                        }}
                      />
                    </StyledTableCell>
                  )}

                  <StyledTableCell>
                    <TableAction tertiary>
                      <Button
                        icon={ArrowRight}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRowClick(entry.assessment._id);
                        }}
                      />
                    </TableAction>
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
        {filteredEntries.length === 0 && (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Text sz="body14" sx={{ color: t.textMuted }}>
              No policies match your filters.
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MonitoringTab;
